import { Badge, type BadgeProps } from "@norican/react";

import type { ActionOutcome, OffboardingStatus, RiskSeverity } from "@/lib/api";

/**
 * One idea of what a status looks like, used everywhere a status is shown.
 *
 * Semantic tone only — never the brand accent, which would compete with the
 * nav for attention on a screen whose whole job is to make one bad row
 * obvious. `partial` and `failed` are the only solid pills, because a
 * half-applied offboarding is the state that costs someone their morning.
 */

const STATUS_STYLE: Record<
  OffboardingStatus,
  { label: string; tone: BadgeProps["tone"]; variant: BadgeProps["variant"] }
> = {
  scheduled: { label: "scheduled", tone: "neutral", variant: "subtle" },
  running: { label: "running", tone: "info", variant: "subtle" },
  phase1_done: { label: "phase 1 done", tone: "info", variant: "subtle" },
  completed: { label: "completed", tone: "success", variant: "subtle" },
  partial: { label: "partial", tone: "warning", variant: "solid" },
  failed: { label: "failed", tone: "danger", variant: "solid" },
  reverted: { label: "reverted", tone: "neutral", variant: "outline" },
  cancelled: { label: "cancelled", tone: "neutral", variant: "outline" },
  // The request and the result, kept apart on purpose: "reinstating" must not
  // read as though somebody already has their access back.
  reinstating: { label: "re-enabling", tone: "info", variant: "subtle" },
  reinstated: { label: "re-enabled", tone: "success", variant: "outline" },
};

const OUTCOME_TONE: Record<ActionOutcome, BadgeProps["tone"]> = {
  submitted: "info",
  succeeded: "success",
  failed: "danger",
  skipped: "neutral",
  denied: "danger",
};

/**
 * What a status means beyond its one-word label, shown next to the badge on
 * the record screen.
 *
 * `scheduled` is deliberately absent: what it means depends on how the worker
 * is deployed, and the record screen renders `describeExecution(...).short`
 * there instead. A sentence here could only be a guess, and this is the one
 * status where a wrong guess matters.
 */
export const STATUS_NOTE: Partial<Record<OffboardingStatus, string>> = {
  reverted: "Reverted in HROps. Nothing was re-enabled in the tenant.",
  reinstating:
    "Queued. The worker re-enables the account on its next pass; it has not happened yet.",
  reinstated: "The account was enabled again in Entra and in on-prem AD.",
  cancelled: "Cancelled before anything ran.",
};

export function StatusBadge({ status }: { status: OffboardingStatus }) {
  const style = STATUS_STYLE[status];
  return (
    <Badge size="xs" tone={style.tone} variant={style.variant}>
      {style.label}
    </Badge>
  );
}

export function OutcomeBadge({ outcome }: { outcome: ActionOutcome }) {
  return (
    <Badge size="xs" tone={OUTCOME_TONE[outcome]} variant="subtle">
      {outcome}
    </Badge>
  );
}

/**
 * Severity is not a status: a flag is a note, not a verdict, so it is plain
 * text at the same weight as the rest of the row rather than a coloured pill.
 */
export function SeverityText({ severity }: { severity: RiskSeverity }) {
  // High is the only severity that changes what the person can do, so it is
  // the only one that earns colour. Giving all three a tone would turn the
  // list into a threat board, which is not what it is for.
  return (
    <span className={severity === "high" ? "text-warning-fg capitalize" : "capitalize"}>
      {severity}
    </span>
  );
}
